import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import {
  CUSTOM_SUBCATEGORY,
  EXISTING_SITE_ANALYSIS,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  PROGRAMMING,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import { useSelector } from 'react-redux';
import { selectWorksheet } from 'spw-redux/modules/calculators/selectors';
import { buildRankMap } from 'spw-shared/utils';
import { MenuItem } from '../styles';
import WorksheetRow from '../../../models/WorksheetRow';

const MIN_HEIGHT = 48;
const MINIMUM_TOP_DISTANCE = 120;

const WORKSHEET_CLOSED_HEIGHT = {
  [PROGRAMMING]: MIN_HEIGHT + 120,
  [EXISTING_SITE_ANALYSIS]: MIN_HEIGHT + 150,
};

export const useWorksheetResize = (
  worksheetHeaderRef: React.MutableRefObject<HTMLDivElement>,
  worksheetContainerRef: React.MutableRefObject<HTMLDivElement>,
  projectType: keyof typeof WORKSHEET_CLOSED_HEIGHT
) => {
  const [worksheetHeight, setWorksheetHeight] = useState(
    WORKSHEET_CLOSED_HEIGHT[projectType]
  );
  const [maxWorksheetHeight, setMaxWorksheetHeight] = useState(
    window.innerHeight - MINIMUM_TOP_DISTANCE
  );

  const [topDistance, setTopDistance] = useState(
    window.innerHeight - worksheetHeight
  );

  useEffect(() => {
    setTopDistance(window.innerHeight - worksheetHeight);
  }, [worksheetHeight]);

  const openOrCloseWorksheet = useCallback(
    (event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      setWorksheetHeight((prevHeight) =>
        prevHeight === maxWorksheetHeight
          ? WORKSHEET_CLOSED_HEIGHT[projectType]
          : maxWorksheetHeight
      );
    },
    [setWorksheetHeight, maxWorksheetHeight, projectType]
  );

  useEffect(() => {
    let position = 0;

    const resize = (e: MouseEvent) => {
      const container = worksheetContainerRef.current;
      const dy = position - e.y;
      position = e.y;
      const h =
        (container ? parseInt(getComputedStyle(container, '').height, 10) : 0) +
        dy;
      setWorksheetHeight(Math.min(Math.max(h, MIN_HEIGHT), maxWorksheetHeight));
    };

    const startResize = (e: MouseEvent) => {
      // prevent text from highlighting behind worksheet
      e.preventDefault();
      position = e.y;
      document.addEventListener('mousemove', resize, false);
    };

    const stopResize = () => {
      document.removeEventListener('mousemove', resize, false);
    };

    const header = worksheetHeaderRef.current;
    if (header) header.addEventListener('mousedown', startResize, false);

    document.addEventListener('mouseup', stopResize, false);
    return () => {
      if (header) header.removeEventListener('mousedown', startResize, false);
      document.removeEventListener('mouseup', stopResize, false);
    };
  }, [maxWorksheetHeight, worksheetContainerRef, worksheetHeaderRef]);

  const autoResize = () => {
    setMaxWorksheetHeight(window.innerHeight - MINIMUM_TOP_DISTANCE);
    setWorksheetHeight(
      Math.min(
        Math.max(
          window.innerHeight - topDistance,
          WORKSHEET_CLOSED_HEIGHT[projectType]
        ),
        // Using calculated value instead of maxWorksheetHeight to avoid issues with set being async
        window.innerHeight - MINIMUM_TOP_DISTANCE
      )
    );
  };

  useEffect(() => {
    window.addEventListener('resize', autoResize);
    return () => {
      window.removeEventListener('resize', autoResize);
    };
  });

  return {
    worksheetHeight,
    openOrCloseWorksheet,
  };
};

export const subcategoriesRankMap: { [key: string]: number } = buildRankMap([
  WORKSTATIONS_SUBCATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  CUSTOM_SUBCATEGORY,
]);

export const useMenuItems = (
  generateDropdownOptions: (subcategoryId?: number) => JSX.Element[],
  handleClick: React.MouseEventHandler<HTMLLIElement>,
  currentUserIsViewOnly: boolean,
  projectSpaceTypeCategory: IProjectSpaceTypeCategory
) => {
  return useMemo(() => {
    let menuItems = [];

    if (
      projectSpaceTypeCategory.defaultSpaceTypeCategory.category ===
      INDIVIDUAL_WORKSPACES_CATEGORY
    ) {
      menuItems = projectSpaceTypeCategory.subcategories
        ?.sort(
          (subcategoryA, subcategoryB) =>
            subcategoriesRankMap[
              subcategoryA.defaultSpaceTypeCategory.category
            ] -
            subcategoriesRankMap[subcategoryB.defaultSpaceTypeCategory.category]
        )
        ?.reduce((itemsList, subcategory: IProjectSpaceTypeSubcategory) => {
          const subcategoryMenuItems = generateDropdownOptions(subcategory.id);

          return itemsList.concat(
            subcategoryMenuItems?.length
              ? [
                  <MenuItem
                    className="title"
                    value="title"
                    onClick={handleClick}
                    key={`${subcategory.id}-title`}
                    disabled={currentUserIsViewOnly}
                  >
                    {subcategory.name}
                  </MenuItem>,
                  ...subcategoryMenuItems,
                ]
              : []
          );
        }, []);
    } else {
      menuItems = generateDropdownOptions();
    }

    const compactedMenuItems = _.compact(menuItems);

    return compactedMenuItems.length
      ? compactedMenuItems
      : [
          <MenuItem className="empty-list-message" key="empty">
            No Unused Spaces
          </MenuItem>,
        ];
  }, [
    currentUserIsViewOnly,
    generateDropdownOptions,
    handleClick,
    projectSpaceTypeCategory.defaultSpaceTypeCategory.category,
    projectSpaceTypeCategory.subcategories,
  ]);
};

export const useWorksheetRowsInUse = () => {
  const worksheet = useSelector(selectWorksheet);

  return useMemo(() => {
    return worksheet.rows.filter((row: WorksheetRow) => !row.removed);
  }, [worksheet.rows]);
};
